'use client'

import React from 'react'
import { Provider } from 'jotai'
import {
  QueryClient,
  QueryClientProvider,
  defaultContext,
} from '@tanstack/react-query'
import { ErrorBoundarySCN } from 'kach-components/src/components/ErrorBoundarySCN'
import { authAtom } from '../atoms/auth.atom'
import { notificationsAtom } from '../atoms/notifications.atom'
import { jobsAtom } from '../atoms/jobs.atom'
import Link from 'next/link'
import NextTopLoader from 'nextjs-toploader'
import setDefaultOptions from 'date-fns/setDefaultOptions'
import { es } from 'date-fns/locale'
import { KachProviderWrapper } from './kach-provider-wrapper'

setDefaultOptions({ locale: es })

export const GLOBAL_SCOPE_JOTAI = 'global'

const ReactQueryDevtoolsProduction = React.lazy(() =>
  import('@tanstack/react-query-devtools/build/lib/index.prod.js').then(
    (d) => ({
      default: d.ReactQueryDevtools,
    }),
  ),
)

interface ProvidersProps {
  children: React.ReactNode
  initialAuth?: any
  initialNotifications?: any
  initialJobs?: any[]
}

export function Providers({
  children,
  initialAuth,
  initialNotifications,
  initialJobs,
}: ProvidersProps) {
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            refetchIntervalInBackground: false,
          },
        },
      }),
  )

  const [showDevtools, setShowDevtools] = React.useState(false)

  React.useEffect(() => {
    // @ts-ignore
    window.toggleDevtools = () => setShowDevtools((old) => !old)
  }, [])

  return (
    <ErrorBoundarySCN Navigator={Link}>
      <Provider
        scope={GLOBAL_SCOPE_JOTAI}
        initialValues={[
          [authAtom, initialAuth],
          [notificationsAtom, initialNotifications],
          [jobsAtom, { jobs: initialJobs || [] }],
        ]}
      >
        <KachProviderWrapper>
          <QueryClientProvider context={defaultContext} client={queryClient}>
            <NextTopLoader
              color='#475465'
              initialPosition={0.08}
              crawlSpeed={200}
              height={3}
              crawl={true}
              showSpinner={false}
              easing='ease'
              speed={200}
              shadow='0 0 10px #475465,0 0 5px #475465'
            />
            {children}

            {showDevtools && (
              <React.Suspense fallback={null}>
                <ReactQueryDevtoolsProduction />
              </React.Suspense>
            )}
          </QueryClientProvider>
        </KachProviderWrapper>
      </Provider>
    </ErrorBoundarySCN>
  )
}
